/**
 * Card for a report.
 */
import * as React from 'react';
import { Calendar, Clock, User, Users } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  REMOTE_REPORT_STATUS_CONFIG,
  REMOTE_REPORT_SCORING_STATUS_CONFIG,
  REMOTE_REPORT_SESSION_TYPE_CONFIG,
  type PerTypeReportPageActions,
} from '@/components/reports/report-configs';
import type { ReportListItem, ReportTab as Tab } from '@/types/remote/report.types';
import {
  deriveReportParticipants,
  getReportActionState,
  resolveReportPermissions,
  formatReportSessionDate,
  isAllReportsTab,
  isInformationReportTab,
  isMeetingReportTab,
  isTutoringReportTab,
} from './report-table-utils';

/** Avatar plus name(s) for students shown on a remote report card. */
function buildStudentsContent(
  students: ReturnType<typeof deriveReportParticipants>['students'],
): React.ReactNode {
  if (students.length === 0) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }
  if (students.length === 1) {
    return (
      <>
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium"
          style={{ backgroundColor: 'var(--color-2-nuance-1)' }}
        >
          {students[0].displayName?.[0] || 'E'}
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-xs text-muted-foreground">Élève</span>
          <span className="text-sm text-foreground truncate">{students[0].displayName}</span>
        </div>
      </>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 cursor-default">
            <Users className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-muted-foreground">Élèves</span>
              <span className="text-sm text-foreground">
                {students.length} élève{students.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-0.5">
            {students.map((s) => (
              <p key={s.userId}>{s.displayName}</p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Props for {@link ReportCard}. */
interface ReportCardProps {
  report: ReportListItem;
  activeTab: Tab;
  onEdit?: (id: string) => void;
  onView?: (id: string) => void;
  onDelete?: (id: string) => void;
  onValidate?: (id: string) => void;
  pageActions?: PerTypeReportPageActions;
}

/** Card row for a remote report in the list. */
export const ReportCard: React.FC<ReportCardProps> = ({
  report,
  activeTab,
  onEdit,
  onView,
  onDelete,
  onValidate,
  pageActions,
}) => {
  const { canEdit, canValidate } = resolveReportPermissions(report.sessionType, pageActions);
  const canDelete = pageActions?.canDeleteRow ?? true;

  const reportStatusConfig = REMOTE_REPORT_STATUS_CONFIG[report.compteRenduStatus];
  const scoringStatusConfig = REMOTE_REPORT_SCORING_STATUS_CONFIG[report.scoringStatus];
  const sessionTypeConfig = REMOTE_REPORT_SESSION_TYPE_CONFIG[report.sessionType];

  const { tutors, students, animator, infoParticipants, welcomeParticipants } =
    deriveReportParticipants(report);

  const { editDisabled, editTooltip, validateDisabled, validateTooltip } =
    getReportActionState(report);

  const sessionDisplayText = formatReportSessionDate(report.sessionDate);

  const meetingParticipants = isInformationReportTab(activeTab)
    ? infoParticipants
    : welcomeParticipants;
  let participantsContent: React.ReactNode;
  if (meetingParticipants.length === 0) {
    participantsContent = (
      <div className="flex items-center gap-1.5">
        <Users className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
        <div className="flex flex-col min-w-0">
          <span className="text-xs text-muted-foreground">Participants</span>
          <span className="text-sm text-foreground">0</span>
        </div>
      </div>
    );
  } else if (meetingParticipants.length === 1) {
    participantsContent = (
      <div className="flex items-center gap-2">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium"
          style={{ backgroundColor: 'var(--color-2-nuance-1)' }}
        >
          {meetingParticipants[0].displayName?.[0] || 'P'}
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-xs text-muted-foreground">Participant</span>
          <span className="text-sm text-foreground truncate">
            {meetingParticipants[0].displayName}
          </span>
        </div>
      </div>
    );
  } else {
    participantsContent = (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 cursor-default">
              <Users className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-muted-foreground">Participants</span>
                <span className="text-sm text-foreground">
                  {meetingParticipants.length} participant
                  {meetingParticipants.length > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-0.5">
              {meetingParticipants.map((p) => (
                <p key={p.userId}>{p.displayName}</p>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const studentsContent = buildStudentsContent(students);

  return (
    <TableCard onClick={() => onView?.(report.id)}>
      <div className="data-card-header">
        {reportStatusConfig && (
          <LabelBadge
            label={reportStatusConfig.label}
            icon={reportStatusConfig.icon}
            variant={reportStatusConfig.badgeVariant ?? 'ghost'}
            color={reportStatusConfig.badgeColor}
            size="sm"
          />
        )}
        {scoringStatusConfig && (
          <LabelBadge
            label={scoringStatusConfig.label}
            icon={scoringStatusConfig.icon}
            variant={scoringStatusConfig.badgeVariant ?? 'ghost'}
            color={scoringStatusConfig.badgeColor}
            size="sm"
          />
        )}
      </div>

      <div className="data-card-content" style={{ overflow: 'hidden' }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium text-foreground text-sm">{sessionDisplayText}</span>
            {report.sessionStartTime && report.sessionEndTime && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5 shrink-0 text-[var(--color-1-nuance-1)]" />
                <span>
                  {report.sessionStartTime.slice(0, 5)} - {report.sessionEndTime.slice(0, 5)}
                </span>
              </div>
            )}
          </div>
        </div>

        {sessionTypeConfig && (isTutoringReportTab(activeTab) || isAllReportsTab(activeTab)) && (
          <div className="mb-2">
            <LabelBadge
              label={sessionTypeConfig.label}
              icon={sessionTypeConfig.icon}
              variant="ghost"
              color={sessionTypeConfig.badgeColor}
              size="sm"
            />
          </div>
        )}

        <div className="space-y-2">
          {isTutoringReportTab(activeTab) && tutors.length > 0 && (
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium"
                style={{ backgroundColor: 'var(--color-1-nuance-1)' }}
              >
                {tutors[0].displayName?.[0] || 'T'}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-muted-foreground">Tuteur</span>
                <span className="text-sm text-foreground truncate">{tutors[0].displayName}</span>
              </div>
            </div>
          )}

          {isTutoringReportTab(activeTab) && (
            <div className="flex items-center gap-2">{studentsContent}</div>
          )}

          {isMeetingReportTab(activeTab) && animator && (
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
                <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-muted-foreground">Animateur</span>
                <span className="text-sm text-foreground truncate">{animator.displayName}</span>
              </div>
            </div>
          )}

          {isMeetingReportTab(activeTab) && !animator && (
            <div className="flex items-center gap-1.5">
              <User className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-muted-foreground">Animateur</span>
                <span className="text-sm">0</span>
              </div>
            </div>
          )}

          {isMeetingReportTab(activeTab) && participantsContent}
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          {onView && (
            <Button
              preset="row-view"
              onClick={(e) => {
                e.stopPropagation();
                onView(report.id);
              }}
            />
          )}

          {canEdit && onEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(report.id);
              }}
              disabled={editDisabled}
              tooltipText={editTooltip}
            />
          )}

          {canValidate && onValidate && (
            <Button
              preset="row-validate"
              onClick={(e) => {
                e.stopPropagation();
                onValidate(report.id);
              }}
              disabled={validateDisabled}
              tooltipText={validateTooltip}
            />
          )}

          {canDelete && onDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(report.id);
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
